/**
 * ObjectTabs — an object page's tabs, one address each
 * (docs/features/journey-visual-language/01_proposal.md §4). The header,
 * the rail and the Now tab carry the whole state, so the other tabs only
 * hold detail. A tab can carry a count badge: amber when something needs
 * attention, red when something is late.
 */
import { Link } from "react-router";
import { cn } from "../primitives/utils";

export interface ObjectTab {
  id: string;
  label: string;
  to: string;
  badge?: { count: number; tone: "amber" | "red" } | null;
}

export function ObjectTabs({
  tabs,
  current,
  ariaLabel,
}: {
  tabs: ObjectTab[];
  current: string;
  ariaLabel: string;
}) {
  return (
    // The baseline is an inset shadow, not a border with the tabs' underline
    // pulled over it by a negative margin: a horizontal scroller also clips
    // vertically, and that 1px overhang gave the strip a 1px vertical scroll
    // range, so a scroll gesture over it nudged the strip before the page
    // (the "wobble"). overflow-y-hidden keeps it that way.
    <nav
      aria-label={ariaLabel}
      className="flex gap-1 overflow-x-auto overflow-y-hidden shadow-[inset_0_-1px_0_var(--border)] [scrollbar-width:none]"
    >
      {tabs.map((tab) => {
        const active = tab.id === current;
        return (
          <Link
            key={tab.id}
            to={tab.to}
            aria-current={active ? "page" : undefined}
            className={cn(
              "flex shrink-0 items-center gap-2 border-b-2 px-4 py-2.5 text-sm transition-colors",
              active
                ? "border-primary font-medium text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground",
            )}
          >
            {tab.label}
            {tab.badge && tab.badge.count > 0 && (
              <span
                className={cn(
                  "inline-flex min-w-5 items-center justify-center rounded-full px-1.5 text-xs font-semibold tabular-nums",
                  tab.badge.tone === "red"
                    ? "bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-200"
                    : "bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-200",
                )}
              >
                {tab.badge.count}
                <span className="sr-only">
                  {tab.badge.tone === "red" ? " late" : " need attention"}
                </span>
              </span>
            )}
          </Link>
        );
      })}
    </nav>
  );
}
